'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { EXPANDED_WIDTH } from './offers';

/**
 * The pill → offers-panel morph mechanism.
 *
 * Extracted from `components/search-bar/text-search/resume-history-chip.tsx`,
 * which shipped this first. The rules below are not stylistic preferences —
 * each one is a bug that was found the hard way there, so read them before
 * changing anything.
 *
 * 1. MEASURE ON THE OPEN CLICK, NEVER ON MOUNT. CSS cannot transition
 *    width/height from `auto`, so a concrete "from" value has to exist before
 *    the box starts growing. Measuring at mount returns garbage: the pill's
 *    label is truncatable (min-content width 0) and the product card is often
 *    still animating in, so the reading comes back a couple of pixels wide and
 *    pins the CTA as a permanent sliver. On the click, layout is always
 *    settled.
 * 2. WRITE THE PIXELS TO THE DOM AND FLUSH IN THE SAME TICK. `void
 *    el.offsetWidth` forces the browser to commit the "from" size before React
 *    renders the "to" size, which is what makes the transition fire at all.
 * 3. NO requestAnimationFrame. rAF does not run in a backgrounded tab, so a
 *    click landing just before a tab switch would leave the panel permanently
 *    un-opened.
 * 4. THE HOLDER IS PINNED TOO. The morph is hoisted out of flow (absolute,
 *    bottom-left) while open; without the holder holding the pill's exact
 *    footprint, the price row under the CTA would jump the moment the panel
 *    opened — the layout shift this whole arrangement exists to prevent.
 * 4b. THE OPEN PANEL IS ANCHORED TO THE BUY COLUMN, NOT TO THE PILL. The pill
 *    is centred in that column, so growing from `left: 0` of the holder left
 *    the panel indented ~50px from the title, description and price — every
 *    other edge on the card — and growing from `bottom: 0` sent it up over the
 *    editorial copy while the price it supersedes stayed on screen underneath
 *    it. `openPanel` measures `[data-offers-anchor]` (the buy column) and
 *    publishes the delta as `--offers-shift-x` / `--offers-gap`; CSS applies them only
 *    while `.is-open`, so the same numbers slide the box back onto the pill on
 *    close. The shift transitions on the same curve as width/height, which is
 *    what keeps it reading as one morph rather than a jump plus a grow.
 * 5. ELEVATE THE STICKY BUY DOCK, NOT THE CTA. The pill sits in a
 *    `position: sticky` bar (`data-product-buy-dock`). Sticky creates a
 *    stacking context at z-auto, so z-[44] on the CTA wrapper cannot paint
 *    above the title/description column (z-40). `data-offers-open` on the
 *    dock is what actually lifts the panel. Do this in the open click, not
 *    in an effect — one painted frame at z-auto is the menu sliding under
 *    the title.
 *
 * 6. THE PILL DOES NOT FADE OUT — IT BECOMES THE PICK LINE. Until 2026-08 the
 *    pill face went to `opacity: 0` and the panel took its box. That is fine
 *    for a click and fatal for hover: the money button would vanish from under
 *    a cursor that was moving toward it and an offer row would land in its
 *    place, one mis-aimed click away from the wrong retailer. The pill now
 *    stays, at the same pixels, as the ink recommended line at the foot of the
 *    panel — which is what makes hover safe, and why `--offers-gap` and
 *    `--offers-pick-h` exist. See `publishColumnMetrics`.
 * 7. HOVER IS A MOUSE-ONLY, INTENT-GATED SHORTCUT, NEVER A REPLACEMENT. Every
 *    path that existed before still works untouched: the trigger's click, the
 *    keyboard, the mobile sheet. Hover only removes the cost of discovering
 *    that we compared retailers at all, on the one input device where that
 *    discovery is free.
 * 8. THE PILL IS MEASURED FROM THE HOLDER, NEVER FROM THE MORPH. The morph is
 *    the one box on the card whose width and height are animated, and
 *    `getBoundingClientRect()` reports the value a transition is currently
 *    PASSING THROUGH — not where it started and not where it is going. Measure
 *    it at the wrong moment and the "pill footprint" is neither the pill nor
 *    the panel. That was not theoretical: hovering off and back on closed the
 *    panel under a stationary cursor, the shrinking box re-armed hover, and
 *    `growPanel` ran ~250ms into the 320ms shrink. The reading was written to
 *    the holder, published as `--offers-pick-h` and pinned as `pinnedSize`, and
 *    since the closed morph stretches to the holder, the NEXT open measured
 *    that taller pill and froze a taller one again. A monotone ratchet:
 *    49.6 → 50.8 → 51.9 → 53.0 → 54.1 → 55.3 → 56.3px over six hover cycles in
 *    a browser, ~180px for a reader, and it never came back down.
 *    The holder cannot lie. It carries no transition, it never leaves flow, and
 *    while the morph IS out of flow its inline size is the footprint we pinned
 *    on the way in. `remeasure` already measured it for exactly this reason.
 *    The pin is now released from the holder as well as from the morph, so a
 *    bad number has nowhere left to persist.
 *
 * Dismissal follows `affiliate-info-icon.tsx`: outside mousedown (capture),
 * Escape, and a passive window scroll all CLOSE the panel. Scroll never
 * repositions it — the panel hangs off a 100vh scroll-snap card, and closing
 * is both cheaper and the only behaviour that cannot fight the snap. A
 * hover-opened panel also closes on `pointerleave` after a grace window; a
 * click-opened one is LATCHED and ignores it.
 */

export type PinnedSize = { width: number; height: number };

export type OffersMorph = {
  /** True while the panel face is showing. */
  open: boolean;
  /** True from the open click until ~360ms after the close animation ends —
   *  i.e. exactly while the morph must stay hoisted out of flow. */
  pinned: boolean;
  /** The pill's measured footprint, or null when the morph is back to natural
   *  sizing. Apply to BOTH the holder and (while closed) the morph element. */
  pinnedSize: PinnedSize | null;
  /** Attach to the element carrying `.offers-morph`. */
  morphRef: React.RefObject<HTMLDivElement>;
  /** Attach to the holder that reserves the pill's footprint in flow. */
  holderRef: React.RefObject<HTMLDivElement>;
  /** Attach to the "N stores" trigger — focus returns here on Escape/back. */
  triggerRef: React.RefObject<HTMLButtonElement>;
  /** Attach to the panel's back button — focus lands here after the grow. */
  backButtonRef: React.RefObject<HTMLButtonElement>;
  /** The "N stores" trigger's click. Toggles, and LATCHES the panel open so it
   *  ignores the pointer leaving. */
  openPanel: () => void;
  closePanel: () => void;
  /** Spread on the element carrying `.offers-morph`. Inert on touch, on a
   *  coarse pointer and below `sm`; see HOVER_QUERY. */
  hoverProps: {
    onPointerOver: (event: React.PointerEvent) => void;
    onPointerLeave: (event: React.PointerEvent) => void;
  };
};

/** Matches the 0.32s grow in `.offers-morph` plus a frame of slack. */
const RELEASE_PIN_MS = 360;
/** Focus the panel once it is actually readable, not mid-grow. */
const FOCUS_PANEL_MS = 320;
/**
 * Don't let the panel's OWN opening dismiss it.
 *
 * The grow perturbs layout, and a browser will emit `scroll` for its own
 * reasons during that — it scrolled the document by 662px the first time this
 * shipped, because the focused trigger collapsed to zero width and the browser
 * chased it back into view. The scroll listener below then read that as "the
 * reader scrolled away" and closed the panel ~40ms after it opened, so clicking
 * "N stores" did nothing at all. `growPanel` now moves focus off the trigger to
 * remove the cause; this is the guard for every other cause, because "the panel
 * moving the page" and "the reader moving the page" are not the same event and
 * only one of them should dismiss anything.
 */
const SCROLL_ARM_MS = 380;
/** Tailwind's `sm` in this repo is 840px, not the framework default 640px
 *  (see tailwind.config.js `theme.screens`). Below it the same open state
 *  renders a portal bottom sheet, so the morph must not measure, pin or grow. */
const MORPH_MIN_WIDTH = 840;
/** Floor for the disclosure strip that fills `--offers-gap`: two 11px lines
 *  at 1.2 leading plus its border and a little air. Only ever reached by a
 *  product whose price block is unusually short. */
const MIN_DISCLOSURE_H = 30;

/**
 * Hover, deliberately kept to a real mouse.
 *
 * `(hover: hover) and (pointer: fine)` and not the viewport width alone: a
 * touchscreen laptop is wider than 840px and reports `pointer: coarse` while a
 * finger is driving it, and a hover-opening purchase menu under a finger is a
 * menu that opens by accident. The width check still applies on top of this —
 * below `sm` the surface is a portal sheet that has no hover story at all.
 */
const HOVER_QUERY = '(hover: hover) and (pointer: fine)';
/** Long enough that a cursor crossing the buy dock on its way elsewhere does
 *  not pop the panel; short enough that deliberate hover feels immediate. */
const HOVER_OPEN_MS = 120;
/** Grace after the pointer leaves, cancelled by re-entry. Pill and panel are
 *  one box, so this only ever absorbs a wobble at the edge — there is no gap
 *  between trigger and menu to cross, and so no safe-triangle corridor. */
const HOVER_CLOSE_MS = 260;

/**
 * The sticky buy bar (`data-product-buy-dock` on the product card). Sticky
 * positioning creates a stacking context at z-auto, so a z-index on the CTA
 * inside it cannot paint above the title/description column (z-40). The dock
 * itself has to be the thing we promote — see globals.css.
 */
const BUY_DOCK_SELECTOR = '[data-product-buy-dock]';

/**
 * The buy column (`ExternalLink`'s root): CTA + price, exactly as wide as the
 * title/description column above it. The open panel aligns its left edge to
 * this element's left edge and its bottom edge to this element's bottom edge,
 * which is the same gutter every other line on the card is aligned to and,
 * respectively, the underside of the price the panel is there to replace.
 */
const BUY_COLUMN_SELECTOR = '[data-offers-anchor]';

/**
 * The card's content column (`w-desktop`, main.tsx) — the title, the
 * description and the image all live inside it, and its width is derived from
 * the viewport, so it is measured rather than duplicated as a constant.
 */
const CONTENT_COLUMN_SELECTOR = '[data-product-content-column]';
const PRODUCT_CARD_SELECTOR = '[data-product-card]';

/**
 * Publish the pill → column deltas as CSS custom properties on the morph.
 *
 * Written as pixels on the element (not React state) for the same reason the
 * "from" size is: the CSS has to be able to read them in the very tick the
 * grow starts.
 */
const publishColumnMetrics = (el: HTMLElement, pill: DOMRect): void => {
  const column = el.closest(BUY_COLUMN_SELECTOR);
  if (!(column instanceof HTMLElement)) return;
  const rect = column.getBoundingClientRect();
  // Sub-pixel or collapsed anchor: leave the pill-anchored defaults alone
  // rather than shifting the panel by a garbage measurement.
  if (rect.width < 1 || rect.height < 1) return;
  // Not rounded: the panel's left edge has to land ON the column's, and this
  // layout resolves to fractions (`calc(100vw …)`). Half a pixel of rounding
  // here is the difference between "aligned" and "nearly aligned", which is
  // the entire point of the shift.
  //
  // Horizontal alignment is taken from the CONTENT column's content box, below,
  // not from this one — see `alignToContentColumn`.

  // THE GAP. Downward-positive, i.e. the way `translate` reads it: the distance
  // from the pill's bottom edge to the column's, which is exactly the price
  // block the open panel is there to supersede. It does three jobs at once —
  //
  //   1. it is the panel's own translate-Y, so the box still lands on the
  //      column's bottom edge, unchanged from the alignment pass;
  //   2. it is the pick line's `bottom` inside that box, which is what puts the
  //      ink line back on the pill's exact pixels rather than near them;
  //   3. it is the disclosure strip's height, so the strip fills the space
  //      instead of a constant that would be wrong for one of the two cases.
  //
  // Which is the whole trick: because the same measured number places the panel
  // and the pick line, they cannot disagree. A discounted product renders a
  // two-line price block (~56px) and a plain one a single line (~35px), and the
  // pick line lands on the button in both.
  //
  // Clamped to the strip's minimum. When a price block is somehow shorter than
  // that the panel hangs a few pixels below the column rather than lifting the
  // pick line off the pill: of the two alignments, the one under the reader's
  // cursor is the one that has to be exact.
  const gap = Math.max(rect.bottom - pill.bottom, MIN_DISCLOSURE_H);
  el.style.setProperty('--offers-gap', `${gap.toFixed(2)}px`);
  // The pick line IS the pill, so it is sized from the pill and never from a
  // constant — this site scales its root font-size with the viewport, so the
  // pill is a different number of pixels tall at 1440 than at 1600.
  el.style.setProperty('--offers-pick-h', `${pill.height.toFixed(2)}px`);

  // ── Horizontal alignment, off the CONTENT column's CONTENT BOX ────────────
  //
  // The content column is `w-desktop pr-desktop`, and those two are the same
  // expression: `w-desktop = 18rem + pr-desktop`. So its BORDER box is ~124px
  // wider than the text it lays out at a 1440 viewport, and a panel matched to
  // that border box overshoots the title and the description by exactly the
  // padding — aligned on the left, visibly past them on the right. Subtracting
  // the padding is what makes both edges land on the copy above.
  //
  // Measured, never duplicated as a constant: `w-desktop` is `calc(100vw …)`
  // with a rem term, so it moves with the viewport AND with this site's
  // root-font-size ramp. The content column is a sibling branch of the buy
  // dock, so it is reached through the card rather than with `closest`.
  const content = el.closest(PRODUCT_CARD_SELECTOR)?.querySelector(CONTENT_COLUMN_SELECTOR);
  if (content instanceof HTMLElement) {
    const box = content.getBoundingClientRect();
    const style = window.getComputedStyle(content);
    const padLeft = parseFloat(style.paddingLeft) || 0;
    const padRight = parseFloat(style.paddingRight) || 0;
    const innerWidth = box.width - padLeft - padRight;
    const innerLeft = box.left + padLeft;
    if (innerWidth >= 1) {
      // An exact match, not a cap: a panel that stopped at EXPANDED_WIDTH
      // inside a wider column would align on the left and miss on the right,
      // which is the half-aligned look this whole pass exists to remove. On a
      // narrow desktop the column bottoms out at `min-width: 18rem` and the
      // rows truncate, which they are built to do.
      el.style.setProperty('--offers-width', `${innerWidth.toFixed(2)}px`);
      el.style.setProperty('--offers-shift-x', `${(innerLeft - pill.left).toFixed(2)}px`);
    }
    // ── Ceiling ──────────────────────────────────────────────────────────────
    // The panel grows UPWARD from the buy column's foot, so left alone a long
    // "Show all" list would climb past the top of the section it belongs to and
    // out over the header. Cap it at the section: from the column's bottom edge
    // (where the panel ends) to the content column's top (where the section
    // starts). With the default three-row window this is never reached — the
    // panel is ~345px against ~690px of section — so the scroll port it implies
    // only ever exists for a list that genuinely needs one.
    const ceiling = rect.bottom - box.top;
    if (ceiling >= 1) el.style.setProperty('--offers-max-h', `${ceiling.toFixed(2)}px`);
  } else {
    // No measurable column: fall back to the pill-anchored default rather than
    // shifting the panel by a garbage number.
    el.style.setProperty('--offers-width', `${EXPANDED_WIDTH}px`);
    el.style.setProperty('--offers-shift-x', `${(rect.left - pill.left).toFixed(2)}px`);
  }
};

/** Anything with a box. Narrower than `HTMLElement` so the rule below can be
 *  exercised without a DOM — see `offers-morph-measurement.test.ts`. */
export type PillBox = { getBoundingClientRect: () => DOMRect };

/**
 * THE ONLY PLACE THE PILL'S FOOTPRINT IS DECIDED. See rule 8 up top.
 *
 * Pure, exported and separate from `growPanel` on purpose: the failure it
 * prevents is geometric — a box read while a CSS transition is passing through
 * it — and neither this repo's jest environment nor jsdom has layout or
 * transitions, so the RULE is the part that can be pinned down in a unit test.
 * The geometry itself is covered by a browser script.
 *
 *   1. Never while the morph is open. Open, the morph IS the panel, so its box
 *      is ~200px of panel rather than ~50px of pill.
 *   2. The holder, not the morph. The morph is the one box on this card whose
 *      size is animated; the holder carries no transition, never leaves flow,
 *      and while the morph is hoisted out of it holds the footprint pinned on
 *      the way in. The morph is a fallback for a holder that never attached.
 *   3. Sub-pixel readings are refused: layout is not settled (or the element is
 *      display:none behind a responsive class), and pinning one would freeze
 *      the CTA at that size.
 */
export const measurePillFootprint = (
  open: boolean,
  holder: PillBox | null,
  morph: PillBox | null,
): DOMRect | null => {
  if (open) return null;
  const source = holder ?? morph;
  if (!source) return null;
  const rect = source.getBoundingClientRect();
  if (rect.width < 1 || rect.height < 1) return null;
  return rect;
};

const setBuyDockElevated = (from: HTMLElement | null, elevated: boolean): HTMLElement | null => {
  const dock = from?.closest(BUY_DOCK_SELECTOR);
  if (!(dock instanceof HTMLElement)) return null;
  dock.toggleAttribute('data-offers-open', elevated);
  return dock;
};

/**
 * Is the desktop morph the surface currently on screen?
 *
 * Evaluated inside handlers and on the click — never during render — so it
 * cannot desync SSR from hydration the way a `useState`-held viewport would.
 */
const morphIsActive = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(`(min-width: ${MORPH_MIN_WIDTH}px)`).matches;

/** Morph on screen AND a real mouse driving it. Evaluated per event, never
 *  during render, for the same SSR reason as `morphIsActive`. */
const hoverIsArmed = (): boolean =>
  morphIsActive() && window.matchMedia(HOVER_QUERY).matches;

export function useOffersMorph(options?: {
  /** Fired on the open click, before the state flip — the buy-intent freeze. */
  onOpen?: () => void;
  /** Fired on every open/close so the caller can promote its own z-index. */
  onOpenChange?: (open: boolean) => void;
}): OffersMorph {
  const { onOpen, onOpenChange } = options ?? {};

  const [open, setOpen] = useState(false);
  const [pinnedSize, setPinnedSize] = useState<PinnedSize | null>(null);

  /**
   * `open`, readable from callbacks that must NOT be rebuilt when it changes.
   *
   * `growPanel` is memoized without `open` in its deps on purpose — the hover
   * timers close over it and need one stable identity — which means the state
   * is invisible to it. This is what it checks instead, so the guard holds for
   * every caller and not only for the one that happens to have fresh state.
   * Assigned during render (same pattern as `onOpenChangeRef` below) AND at the
   * moment the state flips, because the render that would sync it has not
   * happened yet when `growPanel` returns.
   */
  const openRef = useRef(false);
  openRef.current = open;

  const morphRef = useRef<HTMLDivElement>(null);
  const holderRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  // The dock we promoted, so unmount can lower it even if holder is gone.
  const elevatedDockRef = useRef<HTMLElement | null>(null);

  // ── Hover state, all refs: none of it renders, and a re-render per timer
  //    tick would be a re-render of the whole product card. ────────────────
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** True once the reader CLICKED it open. A latched panel ignores
   *  `pointerleave` — it is dismissed by Escape, an outside click or a scroll,
   *  exactly as it was before hover existed. */
  const latchedRef = useRef(false);
  /** Set when a scroll closes the panel. Without it the cursor, which is still
   *  resting on the pill after the panel vanishes, immediately re-satisfies the
   *  hover condition and the panel flickers back open on every wheel tick.
   *  Cleared by the next real `pointerleave`, i.e. when the reader has actually
   *  moved away. */
  const hoverSuppressedRef = useRef(false);

  const clearHoverTimers = useCallback(() => {
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    openTimerRef.current = null;
    closeTimerRef.current = null;
  }, []);

  useEffect(() => clearHoverTimers, [clearHoverTimers]);

  // Kept in a ref so the listeners below never need re-registering when the
  // caller passes a fresh closure on every render.
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  // Notified OUTSIDE the state updater on purpose: `onOpenChange` is another
  // component's setState, and calling it from inside a `setOpen(fn)` updater
  // means updating one component while another renders. Every caller of
  // closePanel is reachable only while the panel is open, and React bails out
  // of an identical state write anyway, so an unconditional call is both safe
  // and idempotent.
  const closePanel = useCallback(() => {
    clearHoverTimers();
    latchedRef.current = false;
    openRef.current = false;
    setOpen(false);
    onOpenChangeRef.current?.(false);
  }, []);

  /**
   * A DELIBERATE dismissal: Escape, an outside click, a scroll.
   *
   * These have to suppress hover on the way out or they do not read as
   * dismissals at all. The cursor is usually still resting on the pill when the
   * panel goes away, and the close itself moves the layout under it — the
   * collapsed "N stores" toggle springs back to full width — which fires a
   * fresh `pointerover` without the reader moving the mouse a pixel. Escape
   * then closed and reopened the panel in the same breath and looked broken.
   * Cleared by the next real `pointerleave`.
   */
  const dismissPanel = useCallback(() => {
    hoverSuppressedRef.current = true;
    closePanel();
  }, [closePanel]);

  const growPanel = useCallback(() => {
    // Already open: there is nothing to grow, and re-running the sequence below
    // would re-fire the buy-intent freeze and yank focus for no reason.
    // `openRef` and not `open` because this callback is deliberately memoized
    // without the state, so the state is invisible to it.
    if (openRef.current) return;
    const el = morphRef.current;
    const holder = holderRef.current;
    if (el && morphIsActive()) {
      // Rule 8. `openRef` is passed even though the guard above already
      // returned: the measurement rule is stated once, in one place, and it is
      // the last line of defence for any caller a later change might add.
      const rect = measurePillFootprint(openRef.current, holder, el);
      // No usable reading: skip the pin and let it open without the animation.
      if (rect) {
        const width = `${rect.width}px`;
        const height = `${rect.height}px`;
        if (holder) {
          holder.style.width = width;
          holder.style.height = height;
        }
        el.style.width = width;
        el.style.height = height;
        // Measured on the open click for the same reason the pill is: the card
        // may still have been animating in at mount, and a stale delta would
        // park the panel a few pixels off the column it is meant to align to.
        publishColumnMetrics(el, rect);
        // Flush: commit the "from" size in this tick so the growth that React
        // renders next has something to animate off. Deliberately not rAF.
        void el.offsetWidth;
        setPinnedSize({ width: rect.width, height: rect.height });
      }
      // Elevate the sticky dock in the same tick as the grow, not in an
      // effect: one painted frame at z-auto is the panel sliding under the
      // title. Stay elevated through the close animation (pin release).
      elevatedDockRef.current = setBuyDockElevated(holder ?? el, true);
      // Hand focus over NOW, not at FOCUS_PANEL_MS. The trigger is about to
      // collapse to zero width (`.offers-pick-toggle`), and a browser scrolls a
      // focused element back into view when its box changes — see
      // SCROLL_ARM_MS. `preventScroll` so the handover itself cannot do the
      // same thing. The later timer still runs and is idempotent.
      if (typeof document !== 'undefined' && document.activeElement === triggerRef.current) {
        backButtonRef.current?.focus({ preventScroll: true });
      }
    }
    onOpen?.();
    // Ahead of `setOpen` so the guard at the top of this function is already
    // true for anything that runs before React commits the render.
    openRef.current = true;
    setOpen(true);
    onOpenChangeRef.current?.(true);
  }, [onOpen]);

  /**
   * The "N stores" trigger, and the only path keyboard and touch have.
   *
   * A click LATCHES: the panel then survives the pointer leaving, and is
   * dismissed the way it always was. Clicking again closes it, which matters
   * now that the open pick line no longer carries a collapse chevron of its
   * own — see buy-now-button.tsx.
   */
  const openPanel = useCallback(() => {
    clearHoverTimers();
    hoverSuppressedRef.current = false;
    if (open) {
      if (latchedRef.current) closePanel();
      else latchedRef.current = true;
      return;
    }
    latchedRef.current = true;
    growPanel();
  }, [open, closePanel, growPanel, clearHoverTimers]);

  const onPointerOver = useCallback(
    (event: React.PointerEvent) => {
      // `pointerover` rather than `pointerenter` so re-entering from a child
      // still counts, and `pointerType` rather than a media query alone so a
      // stylus or a touch that emulates a mouse event cannot arm it.
      if (event.pointerType !== 'mouse') return;
      if (!hoverIsArmed()) return;
      // Cancel a pending close BEFORE the `open` guard below can return.
      //
      // HOVER_CLOSE_MS is documented as a grace "cancelled by re-entry" and it
      // was not: this line used to sit under an early return that fired
      // whenever the panel was open, which is the only state in which a close
      // can be pending. So a cursor that wandered a few pixels off the box and
      // came straight back still lost the panel — and the shrink then moved
      // the box out from under that stationary cursor, which re-armed hover and
      // reopened it mid-animation. That is the gesture that used to corrupt the
      // pill's footprint (rule 8), so this is a fix at the cause as well as a
      // fix to the behaviour the constant already promised.
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      if (open || hoverSuppressedRef.current) return;
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
      openTimerRef.current = setTimeout(() => {
        openTimerRef.current = null;
        // Re-checked at fire time, not at schedule time: 120ms is long enough
        // for a click to have latched it open already.
        if (!hoverSuppressedRef.current) growPanel();
      }, HOVER_OPEN_MS);
    },
    [open, growPanel],
  );

  const onPointerLeave = useCallback(
    (event: React.PointerEvent) => {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      // The reader has moved away, so a scroll-suppressed hover is allowed to
      // arm again the next time they come back.
      hoverSuppressedRef.current = false;
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
      if (!open || latchedRef.current) return;
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      closeTimerRef.current = setTimeout(() => {
        closeTimerRef.current = null;
        closePanel();
      }, HOVER_CLOSE_MS);
    },
    [open, closePanel],
  );

  // Re-measure the column while the panel is open.
  //
  // The click-time reading is taken mid-commit: `onPointerEnter` has just
  // armed the buy-intent freeze, which swaps PriceInfo back to the SSR price,
  // and that re-render can change the column's height by a few pixels AFTER we
  // measured — the panel then sat 5px above the price it is supposed to cover.
  // The same is true of any live price landing while the panel is open. One
  // pass after commit plus a ResizeObserver covers both, and because
  // `left`/`bottom`/`width` are transitioned, a late correction glides rather
  // than jumps.
  useEffect(() => {
    const el = morphRef.current;
    if (!open || !el || !morphIsActive()) return;

    const remeasure = () => {
      const rect = el.getBoundingClientRect();
      // While open, `el` IS the panel — the pill footprint the shift is
      // measured against is the holder's, which is what stayed behind in flow.
      const pill = holderRef.current?.getBoundingClientRect() ?? rect;
      if (pill.width >= 1 && pill.height >= 1) publishColumnMetrics(el, pill);
    };
    remeasure();

    const column = el.closest(BUY_COLUMN_SELECTOR);
    if (typeof ResizeObserver === 'undefined' || !(column instanceof HTMLElement)) return;
    const observer = new ResizeObserver(remeasure);
    observer.observe(column);
    return () => observer.disconnect();
  }, [open]);

  // Release the pin once the shrink has finished, so the pill returns to
  // natural sizing and a later label/count change can never be measured
  // against a stale pinned width.
  useEffect(() => {
    if (open || pinnedSize === null) return;
    const timer = setTimeout(() => setPinnedSize(null), RELEASE_PIN_MS);
    return () => clearTimeout(timer);
  }, [open, pinnedSize]);

  // …and release it from the HOLDER too, which nothing used to do.
  //
  // `growPanel` writes the holder's width/height imperatively (rule 4) and they
  // were never removed, so the holder stayed frozen at whatever was measured
  // for the rest of the page's life. The closed morph is a stretch-aligned flex
  // child of it, so it inherited that height — which is what made a single bad
  // reading permanent AND compounding, because the next open measured the pill
  // it had just enlarged. "Natural sizing" above is only true if the box that
  // reserves the footprint gives it up as well.
  //
  // Keyed on the pin rather than done inside the release timeout: the morph is
  // only back IN FLOW once React has dropped `is-pinned`, and a holder that let
  // go of the footprint one frame earlier would collapse the buy dock — the
  // exact layout shift rule 4 exists to prevent.
  useEffect(() => {
    if (pinnedSize !== null) return;
    const holder = holderRef.current;
    if (!holder) return;
    holder.style.width = '';
    holder.style.height = '';
  }, [pinnedSize]);

  // Keep the dock elevated for the whole pin (open + close animation). If we
  // never pinned (measure skipped), `open` going false is enough to lower it.
  useEffect(() => {
    if (open || pinnedSize !== null) return;
    setBuyDockElevated(holderRef.current ?? elevatedDockRef.current, false);
    elevatedDockRef.current = null;
  }, [open, pinnedSize]);

  useEffect(
    () => () => {
      setBuyDockElevated(elevatedDockRef.current, false);
      elevatedDockRef.current = null;
    },
    [],
  );

  useEffect(() => {
    if (!open) return;

    const onDocMouseDown = (event: MouseEvent) => {
      // Morph only. The mobile sheet is a PORTAL on document.body, i.e. outside
      // the holder by construction — without this guard the compatibility
      // mousedown that every tap fires would read as "clicked outside" and
      // close the sheet the instant a reader touched a row or the expander.
      // Below `sm` dismissal is the scrim button, Escape and the focus trap.
      if (!morphIsActive()) return;
      const holder = holderRef.current;
      if (holder && !holder.contains(event.target as Node)) dismissPanel();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Hand focus back only if it is actually in here. A hover-opened panel
      // never took focus in the first place (see the focus timer below), so
      // moving it to the trigger on Escape would put a focus ring on a control
      // the reader has not touched.
      const holder = holderRef.current;
      const focusWasInside = holder?.contains(document.activeElement);
      dismissPanel();
      // Deferred a tick so the trigger is back to its full width and out of the
      // closing panel's way before it takes focus. Not rAF (rule 3): a tab that
      // backgrounds mid-Escape should still land focus somewhere sane.
      if (focusWasInside) {
        setTimeout(() => triggerRef.current?.focus({ preventScroll: true }), 0);
      }
    };
    // Close, never reposition (affiliate-info-icon precedent). The card is a
    // scroll-snap section; chasing it would fight the snap for no benefit.
    // Only the card-anchored morph needs this — the mobile sheet is `fixed`,
    // so closing it on a stray touch-scroll behind the scrim would be a bug.
    // A scroll EVENT is not the same thing as the reader scrolling.
    //
    // Opening the panel emits one all by itself: the box grows over a card in a
    // scroll-snap container, the container re-snaps, and a focused control that
    // changes size gets chased back into view. Closing on the event alone meant
    // the panel dismissed itself ~30ms after it opened and the "N stores" click
    // did nothing at all. What we actually mean by this dismissal is "the page
    // moved out from under it", so compare positions and ignore the rest.
    //
    // Snapshotted at arm time rather than at open: the grow's own adjustment
    // has settled by then, so this baseline is where the reader really is.
    let anchorY = window.scrollY;
    // Two pixels of slack for sub-pixel snap corrections and elastic overscroll.
    const SCROLL_EPSILON = 2;
    const onScroll = () => {
      if (!morphIsActive()) return;
      if (Math.abs(window.scrollY - anchorY) <= SCROLL_EPSILON) return;
      dismissPanel();
    };

    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    // Armed late, on purpose — see SCROLL_ARM_MS.
    const armScroll = setTimeout(() => {
      anchorY = window.scrollY;
      window.addEventListener('scroll', onScroll, { passive: true });
    }, SCROLL_ARM_MS);
    // The sheet owns its own focus (trap + restore); this only ever reaches the
    // morph's back button, which is display:none below `sm`.
    //
    // `latchedRef` gates it, i.e. only a DELIBERATE open moves focus. Hovering
    // is not a request to go anywhere: stealing focus on hover would drop a
    // focus ring into the panel, and would yank the caret out of whatever the
    // reader was actually using the moment their cursor drifted over the buy
    // dock. Keyboard and click users still land on Back, as they always did.
    const focusTimer = setTimeout(() => {
      if (morphIsActive() && latchedRef.current) {
        backButtonRef.current?.focus({ preventScroll: true });
      }
    }, FOCUS_PANEL_MS);

    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('scroll', onScroll);
      clearTimeout(armScroll);
      clearTimeout(focusTimer);
    };
  }, [open, closePanel, dismissPanel]);

  return {
    open,
    pinned: pinnedSize !== null,
    pinnedSize,
    morphRef,
    holderRef,
    triggerRef,
    backButtonRef,
    openPanel,
    closePanel,
    hoverProps: { onPointerOver, onPointerLeave },
  };
}
